import PresetShortcut from "@/components/preset-shortcut";
import Shortcut from "@/components/shortcut";
import ShortcutAddIcon from "@/components/shortcut-add-icon";
import ShortcutBackIcon from "@/components/shortcut-back-icon";
import ShortcutFolder from "@/components/shortcut-folder";
import styles from "@/components/shortcuts.module.css";
import useShortcuts from "@/hooks/use-shortcuts";
import * as signals from "@/signals";
import { FolderInfo } from "@/types";
import { handleKeyboardClick, isFolder, toggleSecondaryScreen } from "@/utils";
import { SortableContext, rectSortingStrategy } from "@dnd-kit/sortable";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { t } from "i18next";
import { Fragment, JSX, MouseEvent, useState } from "react";

function Shortcuts(): JSX.Element {
	const [isScrolled, setIsScrolled] = useState<boolean>(false);
	const [isScrolledToBottom, setIsScrolledToBottom] =
		useState<boolean>(false);

	const { data, isError } = useShortcuts();

	const handleContainerClick = (): void => {
		void toggleSecondaryScreen();
	};

	const handleScroll = (event: MouseEvent<HTMLDivElement>): void => {
		const container = event.currentTarget;
		setIsScrolled(container.scrollTop > 10);
		setIsScrolledToBottom(
			container.scrollHeight -
				(container.scrollTop +
					parseInt(window.getComputedStyle(container).height)) <=
				25,
		);
	};

	const dragIds: string[] = [];
	const shortcutUniqueKeys = new Set<string | number>(); // avoid duplicate items
	const targetFolder = signals.currentFolder.value
		? (data?.children.find(
				(item) => item.id === signals.currentFolder.value?.id,
			) as FolderInfo)
		: data;
	const shortcutElements = targetFolder?.children.map((item, index) => {
		const key = item.id || index;
		if (shortcutUniqueKeys.has(key)) {
			console.warn("Duplicate key skipped:", key);
			return null;
		}
		shortcutUniqueKeys.add(key);

		const dragId = "shortcut-" + key.toString() + "-" + item.itemType;
		dragIds.push(dragId);

		return (
			<Fragment key={key}>
				{item.itemType === "bookmark" && (
					<Shortcut
						dragId={dragId}
						isDraggable={true}
						isInDock={false}
						item={item}
					/>
				)}
				{isFolder(item) && (
					<ShortcutFolder
						dragId={dragId}
						folder={item}
					/>
				)}
				{item.itemType === "preset" && item.title && (
					<PresetShortcut
						dragId={dragId}
						isDraggable={true}
						isInDock={false}
						id={item.id}
						title={item.title}
					/>
				)}
			</Fragment>
		);
	});

	return (
		<SortableContext
			items={dragIds}
			strategy={rectSortingStrategy}
		>
			<div
				className={clsx(
					styles["shortcuts-container"],
					isScrolled && styles["scrolled"],
					isScrolledToBottom && styles["scrolled-to-bottom"],
					signals.generalSettings.value
						.shouldShowMoreShortcutsPerRow && styles["wide-screen"],
				)}
				role="button"
				tabIndex={0}
				onClick={handleContainerClick}
				onKeyDown={handleKeyboardClick(() => {})} // only allow mouse click
				onScroll={handleScroll}
			>
				{isError && (
					<div className={styles["error-info"]}>
						<FontAwesomeIcon icon={faTriangleExclamation} />
						{t("errorOccurredSyncShortcuts")}
					</div>
				)}
				{signals.currentFolder.value && <ShortcutBackIcon />}
				{shortcutElements}
				<ShortcutAddIcon />
			</div>
		</SortableContext>
	);
}

export default Shortcuts;
